"use client"

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { EnhancedReviewForm } from "@/components/enhanced-review-form"
import { usePlatform } from "@/hooks/use-platform"
import { Star, X } from "lucide-react"
import { Button } from "@/components/ui/button"

interface AddReviewModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  spotId: string
  spotName: string
  spotLocation?: string
  onReviewAdded: () => void
}

export function AddReviewModal({
  open,
  onOpenChange,
  spotId,
  spotName,
  spotLocation,
  onReviewAdded,
}: AddReviewModalProps) {
  const { isMobile } = usePlatform()

  const handleReviewAdded = () => {
    onReviewAdded()
    onOpenChange(false)
  }

  const title = (
    <div className="flex items-center gap-3">
      <div className="p-2.5 bg-yellow-50 dark:bg-yellow-900/20 rounded-2xl shadow-sm border border-yellow-100/50 dark:border-yellow-900/30">
        <Star className="w-6 h-6 text-yellow-500 fill-yellow-500" />
      </div>
      <div className="flex flex-col">
        <span className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Review Spot</span>
        <span className="text-[10px] font-bold text-blue-500 uppercase tracking-widest">{spotName}</span>
      </div>
    </div>
  )

  const description = "Share your experience and help the community with real-time conditions!"

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[92vh] bg-white dark:bg-slate-900 border-none rounded-t-[3rem]">
          <div className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-slate-200 dark:bg-slate-800 mt-4 mb-2" />
          <DrawerHeader className="px-6 pb-2">
            <DrawerTitle className="sr-only">Review {spotName}</DrawerTitle>
            <div className="flex items-center justify-between">
              {title}
            </div>
            <DrawerDescription className="text-[11px] font-medium text-slate-500 dark:text-slate-400 mt-2 text-left">
              {description}
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-6 pb-12 overflow-y-auto mt-4">
            <EnhancedReviewForm
              surfSpotId={spotId}
              spotLocation={spotLocation}
              onReviewAdded={handleReviewAdded}
              onCancel={() => onOpenChange(false)}
              isMobile={true}
            />
          </div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900 border-none rounded-[3rem] shadow-2xl p-0 gap-0">
        <div className="p-8 space-y-8 relative">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            className="absolute right-6 top-6 rounded-full bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors z-10"
          >
            <X className="w-5 h-5 text-slate-500" />
          </Button>
          
          <DialogHeader className="p-0">
            <DialogTitle className="sr-only">Review {spotName}</DialogTitle>
            {title}
            <DialogDescription className="text-[11px] font-medium text-slate-500 dark:text-slate-400 mt-2 text-left">
              {description}
            </DialogDescription>
          </DialogHeader>

          <EnhancedReviewForm
            surfSpotId={spotId}
            spotLocation={spotLocation}
            onReviewAdded={handleReviewAdded}
            onCancel={() => onOpenChange(false)}
            isMobile={false}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
